/**
 * 数字滚动动画效果
 * 使用方法：
 * 1. 给要滚动的元素添加 data-count-up 属性，并设置最终要显示的数字
 * 2. 可以通过 data-duration 设置动画持续时间（毫秒）
 * 3. 可以通过 data-suffix 设置后缀（如"+"）
 */
class NumberCounter {
  constructor() {
    this.counters = document.querySelectorAll('[data-count-up]');
    this.options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    this.init();
  }

  // 缓动函数，让动画更加自然
  easeOutQuart(x) {
    return 1 - Math.pow(1 - x, 4);
  }

  init() {
    if (!this.counters.length) return;

    // 创建交叉观察器，当元素进入视口时开始动画
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const counter = entry.target;
          this.animateCounter(counter);
          // 只执行一次
          this.observer.unobserve(counter);
        }
      });
    }, this.options);

    // 观察所有计数器元素
    this.counters.forEach(counter => {
      this.observer.observe(counter);
    });
  }

  animateCounter(counter) {
    // 获取配置
    const finalValue = parseInt(counter.getAttribute('data-count-up').replace(/,/g, ''));
    const duration = parseInt(counter.getAttribute('data-duration') || 1500);
    const suffix = counter.getAttribute('data-suffix') || '';

    // 保存初始值和最终值
    const startValue = 0;
    let startTime = null;

    // 添加计数中的类名，用于样式变化
    counter.classList.add('counting');

    // 根据数字大小确定阶段显示，避免大数字从0逻一计数
    const getStartingValue = (value) => {
      if (value >= 1000000) return value * 0.8; // 百万级数字从80%开始
      if (value >= 100000) return value * 0.6; // 十万级数字从60%开始
      if (value >= 10000) return value * 0.4; // 万级数字从40%开始
      if (value >= 1000) return value * 0.2;  // 千级数字从20%开始
      return 0; // 小数字从0开始
    };

    const actualStartValue = getStartingValue(finalValue);

    // 格式化数字，根据大小调整显示方式
    const formatNumber = (num) => {
      if (num >= 1000000) {
        return `${Math.floor(num / 1000000).toLocaleString()},${(`000000${Math.floor(num % 1000000)}`).slice(-6)}`;
      } else if (num >= 1000) {
        return `${Math.floor(num / 1000).toLocaleString()},${(`000${Math.floor(num % 1000)}`).slice(-3)}`;
      }
      return num.toLocaleString();
    };    // 动画函数
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;

      // 计算动画进度（使用缓动函数让动画更自然）
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easedProgress = this.easeOutQuart(progress);

      // 计算当前值，使用新的起始值
      let currentValue = Math.floor(easedProgress * (finalValue - actualStartValue) + actualStartValue);

      // 显示当前值
      counter.textContent = formatNumber(currentValue) + suffix;      // 如果动画未完成，继续请求动画帧
      if (progress < 1) {
        window.requestAnimationFrame(animate);
      } else {
        // 确保最后显示的是准确的最终值
        counter.textContent = counter.getAttribute('data-original-text') ||
          (formatNumber(finalValue) + suffix);
        // 移除计数中的类名
        setTimeout(() => {
          counter.classList.remove('counting');
        }, 300);
      }
    };

    // 保存原始文本，以便在动画结束时恢复
    if (!counter.getAttribute('data-original-text')) {
      counter.setAttribute('data-original-text', counter.textContent);
    }

    // 开始动画
    window.requestAnimationFrame(animate);
  }
}

// 当DOM加载完成后初始化
document.addEventListener('DOMContentLoaded', () => {
  new NumberCounter();
});